// React imports
import React, { useEffect, useMemo, useState } from "react";

// External library imports
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  deleteData,
  fetchData,
  postData,
  putData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import {
  kpiLinkageDetailUrl,
  kpiLinkageTreeUrl,
  kpiLinkagesUrl,
  kpiMasterUrl,
} from "../../../Service/Apis";
import KPITreeView, { KPITreeNode } from "./components/KPITreeView";

interface KPIMasterOption {
  kpi_id: string;
  kpi_code: string | null;
  question: string;
  threshold: number | null;
  weightage: number;
  section_name: string;
  template_name: string;
  designation_name: string;
}

interface KPILinkage {
  linkage_id: string;
  child_kpi: string;
  child_kpi_code: string | null;
  child_kpi_title: string;
  parent_kpi: string;
  parent_kpi_code: string | null;
  parent_kpi_title: string;
  weight: number;
  cycle_start_date: string | null;
  cycle_end_date: string | null;
  is_active: boolean;
  created_by_name: string | null;
  created_date: string;
  weight_warning: string | null;
}

interface LinkageFormState {
  linkage_id: string | null;
  child_kpi: string;
  parent_kpi: string;
  weight: string;
  cycle_start_date: string;
  cycle_end_date: string;
}

const emptyForm: LinkageFormState = {
  linkage_id: null,
  child_kpi: "",
  parent_kpi: "",
  weight: "",
  cycle_start_date: "",
  cycle_end_date: "",
};

const kpiLabel = (kpi: KPIMasterOption): string =>
  `${kpi.kpi_code || "(no code)"} — ${kpi.question.slice(0, 60)}`;

const KPILinkageManager: React.FC = () => {
  const showToast = useCustomToast();
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const [kpis, setKpis] = useState<KPIMasterOption[]>([]);
  const [linkages, setLinkages] = useState<KPILinkage[]>([]);
  const [tree, setTree] = useState<KPITreeNode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedKpiId, setSelectedKpiId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [form, setForm] = useState<LinkageFormState>(emptyForm);
  const [saving, setSaving] = useState<boolean>(false);

  const [deleteTarget, setDeleteTarget] = useState<KPILinkage | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleShowToast = (message: string, variant: ToastVariant): void => {
    showToast(message, variant, true);
  };

  const kpiById = useMemo(() => {
    const map: Record<string, KPIMasterOption> = {};
    kpis.forEach((k) => (map[k.kpi_id] = k));
    return map;
  }, [kpis]);

  const fetchAll = async (): Promise<void> => {
    setLoading(true);
    try {
      const [kpiResponse, linkageResponse, treeResponse] = await Promise.all([
        dispatch(fetchData(kpiMasterUrl)),
        dispatch(fetchData(kpiLinkagesUrl)),
        dispatch(fetchData(kpiLinkageTreeUrl)),
      ]);

      setKpis(Array.isArray(kpiResponse) ? kpiResponse : kpiResponse?.results || []);
      setLinkages(Array.isArray(linkageResponse) ? linkageResponse : linkageResponse?.results || []);
      setTree(treeResponse?.tree || []);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      handleShowToast(
        axiosError.response?.data?.error || axiosError.message || "Failed to load KPI linkage data",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateForm = (): void => {
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEditForm = (linkage: KPILinkage): void => {
    setForm({
      linkage_id: linkage.linkage_id,
      child_kpi: linkage.child_kpi,
      parent_kpi: linkage.parent_kpi,
      weight: String(linkage.weight),
      cycle_start_date: linkage.cycle_start_date || "",
      cycle_end_date: linkage.cycle_end_date || "",
    });
    setFormOpen(true);
  };

  const handleSelectChange = (field: "child_kpi" | "parent_kpi") => (e: SelectChangeEvent<string>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (): Promise<void> => {
    if (!form.child_kpi || !form.parent_kpi) {
      handleShowToast("Please select both a Child KPI and a Parent KPI.", "error");
      return;
    }
    if (form.child_kpi === form.parent_kpi) {
      handleShowToast("A KPI cannot link to itself.", "error");
      return;
    }
    const weightValue = parseFloat(form.weight);
    if (Number.isNaN(weightValue) || weightValue <= 0) {
      handleShowToast("Please enter a weight greater than 0 (e.g. 0.33 or 0.5).", "error");
      return;
    }

    const payload = {
      child_kpi: form.child_kpi,
      parent_kpi: form.parent_kpi,
      weight: weightValue,
      cycle_start_date: form.cycle_start_date || null,
      cycle_end_date: form.cycle_end_date || null,
    };

    setSaving(true);
    try {
      if (form.linkage_id) {
        const response = (await dispatch(
          putData(kpiLinkageDetailUrl(form.linkage_id), payload)
        )) as KPILinkage;
        if (response?.weight_warning) {
          handleShowToast(response.weight_warning, "info");
        }
        handleShowToast("Linkage updated successfully.", "success");
      } else {
        const response = (await dispatch(postData(kpiLinkagesUrl, payload))) as KPILinkage;
        if (response?.weight_warning) {
          handleShowToast(response.weight_warning, "info");
        }
        handleShowToast("Linkage created successfully.", "success");
      }
      setFormOpen(false);
      setForm(emptyForm);
      await fetchAll();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: any }; message?: string };
      const data = axiosError.response?.data;
      const errorMessage =
        (data && (data.child_kpi?.[0] || data.parent_kpi?.[0] || data.weight?.[0] || data.error)) ||
        axiosError.message ||
        "Failed to save linkage.";
      handleShowToast(errorMessage, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteData(kpiLinkageDetailUrl(deleteTarget.linkage_id)));
      handleShowToast("Linkage deleted successfully.", "success");
      setDeleteTarget(null);
      await fetchAll();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      handleShowToast(
        axiosError.response?.data?.error || axiosError.message || "Failed to delete linkage.",
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      <PageTitle>KPI Linkage Manager</PageTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Link existing KPIs into a simple parent-child hierarchy (e.g. CEO KPI → Department/Role KPIs →
        Team KPIs → Individual KPIs). Each linkage carries a weight used in the contribution formula:{" "}
        <strong>%Derived = (Rating / Threshold) × Weight</strong>. This is informational only and never
        changes actual ratings or statuses.
      </Typography>

      <Grid container spacing={3}>
        {/* Tree view */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Hierarchy Tree
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <KPITreeView
              nodes={tree}
              selectedKpiId={selectedKpiId}
              onSelectNode={(node) => setSelectedKpiId(node.kpi_id)}
            />
          </Paper>
        </Grid>

        {/* Linkage list + form */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Linkages</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={openCreateForm}
                >
                  New Linkage
                </Button>
              </Box>

              {linkages.length === 0 ? (
                <Alert severity="info">No linkages created yet.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Child KPI</TableCell>
                        <TableCell>Parent KPI</TableCell>
                        <TableCell align="right">Weight</TableCell>
                        <TableCell>Cycle</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linkages.map((linkage) => (
                        <TableRow key={linkage.linkage_id} hover>
                          <TableCell>
                            <Tooltip title={linkage.child_kpi_title}>
                              <span>{linkage.child_kpi_code || linkage.child_kpi.slice(0, 8)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={linkage.parent_kpi_title}>
                              <span>{linkage.parent_kpi_code || linkage.parent_kpi.slice(0, 8)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            {linkage.weight}
                            {linkage.weight_warning && (
                              <Tooltip title={linkage.weight_warning}>
                                <Chip
                                  size="small"
                                  color="warning"
                                  label="!"
                                  sx={{ ml: 1, height: 18, minWidth: 18 }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {linkage.cycle_start_date && linkage.cycle_end_date
                              ? `${linkage.cycle_start_date} → ${linkage.cycle_end_date}`
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEditForm(linkage)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setDeleteTarget(linkage)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create/Edit Linkage Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.linkage_id ? "Edit Linkage" : "New Linkage"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Child KPI</InputLabel>
              <Select
                label="Child KPI"
                value={form.child_kpi}
                onChange={handleSelectChange("child_kpi")}
              >
                {kpis.map((kpi) => (
                  <MenuItem key={kpi.kpi_id} value={kpi.kpi_id}>
                    {kpiLabel(kpi)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Parent KPI</InputLabel>
              <Select
                label="Parent KPI"
                value={form.parent_kpi}
                onChange={handleSelectChange("parent_kpi")}
              >
                {kpis.map((kpi) => (
                  <MenuItem key={kpi.kpi_id} value={kpi.kpi_id}>
                    {kpiLabel(kpi)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Weight"
              type="number"
              size="small"
              placeholder="e.g. 0.33"
              inputProps={{ min: 0, step: 0.001 }}
              value={form.weight}
              onChange={(e) => setForm((prev) => ({ ...prev, weight: e.target.value }))}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Cycle Start Date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.cycle_start_date}
                onChange={(e) => setForm((prev) => ({ ...prev, cycle_start_date: e.target.value }))}
              />
              <TextField
                label="Cycle End Date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.cycle_end_date}
                onChange={(e) => setForm((prev) => ({ ...prev, cycle_end_date: e.target.value }))}
              />
            </Box>

            {form.parent_kpi && kpiById[form.parent_kpi] && (
              <Typography variant="caption" color="text.secondary">
                Parent threshold: {kpiById[form.parent_kpi].threshold ?? "not set"}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Linkage</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the linkage between{" "}
            <strong>{deleteTarget?.child_kpi_code || deleteTarget?.child_kpi}</strong> and{" "}
            <strong>{deleteTarget?.parent_kpi_code || deleteTarget?.parent_kpi}</strong>? This action is
            audited and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KPILinkageManager;
